import React from "react";

/**
 * FloralAccent - hand-drawn style botanical SVGs used as decorative accents
 * around the page. Variants: rose, sprig, wildflower, leaves.
 */
export const FloralAccent = ({ variant = "rose", className = "", style }) => {
    const common = {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: "0 0 200 200",
        className,
        style,
        "aria-hidden": true,
    };

    if (variant === "rose") {
        return (
            <svg {...common}>
                <g fill="none" stroke="#B8C7B9" strokeWidth="2" strokeLinecap="round">
                    <path d="M100 180 C 100 140, 90 120, 70 110" />
                    <path d="M100 160 C 110 140, 130 130, 150 135" />
                    <path d="M70 110 C 55 108, 45 100, 45 92" />
                    <path d="M150 135 C 165 130, 172 118, 170 108" />
                </g>
                <g>
                    <ellipse cx="45" cy="88" rx="9" ry="5" fill="#B8C7B9" transform="rotate(-30 45 88)" />
                    <ellipse cx="170" cy="104" rx="9" ry="5" fill="#B8C7B9" transform="rotate(35 170 104)" />
                </g>
                <g transform="translate(100 70)">
                    <circle r="42" fill="#FDB8C0" opacity="0.55" />
                    <circle r="32" fill="#F4DCD6" />
                    <circle r="22" fill="#FDB8C0" opacity="0.75" />
                    <circle r="12" fill="#E2959D" opacity="0.85" />
                    <circle r="5" fill="#D47C85" />
                </g>
            </svg>
        );
    }

    if (variant === "sprig") {
        return (
            <svg {...common}>
                <g fill="none" stroke="#B8C7B9" strokeWidth="2.2" strokeLinecap="round">
                    <path d="M100 20 C 100 80, 100 140, 100 190" />
                    {Array.from({ length: 8 }).map((_, i) => {
                        const y = 40 + i * 18;
                        return (
                            <g key={i}>
                                <path d={`M100 ${y} C 80 ${y - 8}, 65 ${y - 4}, 55 ${y + 6}`} />
                                <path d={`M100 ${y + 6} C 120 ${y - 2}, 135 ${y + 2}, 145 ${y + 12}`} />
                            </g>
                        );
                    })}
                </g>
                {Array.from({ length: 8 }).map((_, i) => {
                    const y = 40 + i * 18;
                    return (
                        <g key={i}>
                            <ellipse cx="52" cy={y + 6} rx="10" ry="4.5" fill="#C7D3C4" transform={`rotate(-25 52 ${y + 6})`} />
                            <ellipse cx="148" cy={y + 12} rx="10" ry="4.5" fill="#C7D3C4" transform={`rotate(25 148 ${y + 12})`} />
                        </g>
                    );
                })}
            </svg>
        );
    }

    if (variant === "wildflower") {
        return (
            <svg {...common}>
                <g fill="none" stroke="#B8C7B9" strokeWidth="2" strokeLinecap="round">
                    <path d="M100 190 C 100 140, 90 110, 80 90" />
                    <path d="M100 170 C 110 150, 125 140, 140 135" />
                    <path d="M100 150 C 88 130, 75 125, 60 128" />
                </g>
                {[{ cx: 80, cy: 80, c: "#FFDAB9" }, { cx: 140, cy: 130, c: "#FDB8C0" }, { cx: 60, cy: 126, c: "#F4DCD6" }].map((p, i) => (
                    <g key={i} transform={`translate(${p.cx} ${p.cy})`}>
                        {Array.from({ length: 6 }).map((_, j) => {
                            const a = (j * 60 * Math.PI) / 180;
                            return (
                                <ellipse
                                    key={j}
                                    cx={Math.cos(a) * 12}
                                    cy={Math.sin(a) * 12}
                                    rx="9"
                                    ry="5"
                                    fill={p.c}
                                    opacity="0.85"
                                    transform={`rotate(${j * 60} ${Math.cos(a) * 12} ${Math.sin(a) * 12})`}
                                />
                            );
                        })}
                        <circle r="6" fill="#E2959D" />
                    </g>
                ))}
            </svg>
        );
    }

    // leaves
    return (
        <svg {...common}>
            <g fill="none" stroke="#B8C7B9" strokeWidth="2" strokeLinecap="round">
                <path d="M20 180 Q 100 100, 180 20" />
            </g>
            {Array.from({ length: 7 }).map((_, i) => {
                const t = i / 6;
                const x = 20 + (180 - 20) * t;
                const y = 180 - (180 - 20) * t;
                const rot = -45 + (i % 2 === 0 ? -18 : 18);
                return (
                    <ellipse
                        key={i}
                        cx={x}
                        cy={y}
                        rx="16"
                        ry="6.5"
                        fill={i % 2 === 0 ? "#C7D3C4" : "#B8C7B9"}
                        transform={`rotate(${rot} ${x} ${y})`}
                    />
                );
            })}
        </svg>
    );
};

export default FloralAccent;
